import {
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  MailOutlined,
  TagsOutlined,
  TeamOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  Descriptions,
  Form,
  Modal,
  message,
  Progress,
  Result,
  Select,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import Papa from 'papaparse';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { ATTENDEE_TYPES, attendeeTypeLabelKey } from '../i18n/attendeeType';

const { Title, Text, Link } = Typography;
const { Dragger } = Upload;
const { Option } = Select;
const { Step } = Steps;

interface TicketImportModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface ImportResult {
  success: number;
  failed: number;
  errors: Array<{
    row: number;
    error: string;
  }>;
  created: {
    users: number;
    companies: number;
    tickets: number;
  };
  skipped: {
    existingTickets: number;
  };
  // Csak akkor jön, ha a szervező kérte a jegylevél kiküldését (#104).
  notifications?: {
    sent: number;
    failed: number;
    failedTickets: string[];
    skippedNoEmail: number;
    // Kiment a levél, de QR nélkül — a #102 óta a levél maga a belépő, tehát
    // ezek a résztvevők az ajtónál nem olvashatók be.
    withoutQr: string[];
  };
}

// A CSV FEJLÉCNEVEI — ezek NEM feliratok, hanem szerződés a beolvasóval.
// A `tickets-import.service.ts` betű szerint ezeket a mezőneveket olvassa a
// sorokból (`row.firstName`, `row.ticketType`, …), és a letölthető sablon is
// ezekkel készül. Lefordítva a szervező magyar fejlécű táblázatot állítana
// össze, az importálás pedig minden során elhasalna. Ezért állnak külön, a
// szótáron kívül, és ezért interpolációval kerülnek a mondatba.
const REQUIRED_CSV_COLUMNS = 'email, firstName, lastName, ticketType';
const OPTIONAL_CSV_COLUMNS = 'phone, companyName, companyTaxNumber, companyAddress';

const TicketImportModal: React.FC<TicketImportModalProps> = ({ visible, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  useEffect(() => {
    if (visible) {
      fetchEvents();
    }
  }, [visible]);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data.data || response.data);
    } catch (error) {
      message.error(t('ticketImport.eventsLoadFailed'));
    }
  };

  const handleFileUpload = (file: File) => {
    setFile(file);

    // Parse CSV for preview (first 5 rows)
    Papa.parse(file, {
      header: true,
      preview: 5,
      complete: (results) => {
        setPreview(results.data);
      },
      error: (error) => {
        message.error(t('ticketImport.csvParseError', { message: error.message }));
      },
    });

    // Parse CSV to get total row count
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        setTotalRows(results.data.length);
      },
      error: (error) => {
        console.error('Error getting row count:', error);
      },
    });

    return false; // Prevent automatic upload
  };

  const downloadTemplate = () => {
    window.open(`${api.defaults.baseURL}/tickets/import/template`, '_blank');
  };

  const handleImport = async (values: any) => {
    if (!file) {
      message.error(t('ticketImport.selectFile'));
      return;
    }

    console.log('handleImport called with values:', values);
    console.log('eventId value:', values.eventId);

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('eventId', values.eventId);
    formData.append('importCompanies', values.importCompanies ? 'true' : 'false');
    formData.append('sendNotifications', values.sendNotifications ? 'true' : 'false');
    // #165 — egy kategória az EGÉSZ fájlra. A szerver enum-ellenőrzést fut rá,
    // tehát elgépelt érték 400-at ad, nem néma átsorolást.
    formData.append('attendeeType', values.attendeeType);

    // Debug: Log what's in FormData
    console.log('FormData contents:');
    Array.from(formData.entries()).forEach(([key, value]) => {
      console.log(`${key}:`, value);
    });

    try {
      const response = await api.post('/tickets/import/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setResult(response.data);
      setCurrentStep(2);

      if (response.data.success > 0) {
        message.success(t('ticketImport.importSuccess', { count: response.data.success }));
        onSuccess?.();
      }

      if (response.data.failed > 0) {
        message.warning(t('ticketImport.importFailedCount', { count: response.data.failed }));
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || t('ticketImport.importFailed'));
    } finally {
      setImporting(false);
    }
  };

  const resetModal = () => {
    setCurrentStep(0);
    setFile(null);
    setPreview([]);
    setResult(null);
    form.resetFields();
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const previewColumns =
    preview.length > 0
      ? Object.keys(preview[0]).map((key) => ({
          title: key,
          dataIndex: key,
          key: key,
          width: 150,
          ellipsis: true,
        }))
      : [];

  return (
    <Modal
      title={
        <Space>
          <UploadOutlined />
          <span>{t('ticketImport.title')}</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      width={900}
      footer={null}
    >
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        <Step title={t('ticketImport.stepUpload')} icon={<UploadOutlined />} />
        <Step title={t('ticketImport.stepReview')} icon={<FileTextOutlined />} />
        <Step title={t('ticketImport.stepResults')} icon={<CheckCircleOutlined />} />
      </Steps>

      {currentStep === 0 && (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            importCompanies: false,
            sendNotifications: false,
            // A mai viselkedés: az import eddig sem adott kategóriát, és a séma
            // alapértelmezése `PARTICIPANT` volt. Előreválasztva ugyanaz a jegy
            // jön létre, mint eddig — csak most látszik is, melyik.
            attendeeType: 'PARTICIPANT',
          }}
          onFinish={(values) => {
            if (!file) {
              message.error(t('ticketImport.uploadCsvFirst'));
              return;
            }
            setCurrentStep(1);
          }}
        >
          <Alert
            message={t('ticketImport.instructionsTitle')}
            description={
              <Space direction="vertical">
                <Text>{t('ticketImport.requiredColumns', { columns: REQUIRED_CSV_COLUMNS })}</Text>
                <Text>{t('ticketImport.optionalColumns', { columns: OPTIONAL_CSV_COLUMNS })}</Text>
                <Link onClick={downloadTemplate}>
                  <DownloadOutlined /> {t('ticketImport.downloadTemplate')}
                </Link>
              </Space>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            name="eventId"
            label={t('ticketImport.selectEvent')}
            rules={[{ required: true, message: t('ticketImport.selectEventRequired') }]}
          >
            <Select
              placeholder={t('ticketImport.selectEventPlaceholder')}
              size="large"
              showSearch
              optionFilterProp="children"
            >
              {events.map((event) => (
                <Option key={event.id} value={event.id}>
                  {event.title} ({new Date(event.startDate).toLocaleDateString()})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="attendeeType"
            label={t('ticketImport.attendeeType')}
            extra={t('ticketImport.attendeeTypeHint')}
            rules={[{ required: true }]}
          >
            <Select size="large">
              {ATTENDEE_TYPES.map((value) => {
                const labelKey = attendeeTypeLabelKey(value);
                return (
                  <Option key={value} value={value}>
                    {labelKey ? t(labelKey) : value}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item label={t('ticketImport.csvFile')}>
            <Dragger
              accept=".csv"
              beforeUpload={handleFileUpload}
              onRemove={() => {
                setFile(null);
                setPreview([]);
                setTotalRows(0);
              }}
              fileList={
                file
                  ? [
                      {
                        uid: '-1',
                        name: file.name,
                        status: 'done',
                        size: file.size,
                      } as UploadFile,
                    ]
                  : []
              }
              maxCount={1}
            >
              <p className="ant-upload-drag-icon">
                <FileTextOutlined style={{ fontSize: 48 }} />
              </p>
              <p className="ant-upload-text">{t('ticketImport.dropzoneText')}</p>
              <p className="ant-upload-hint">{t('ticketImport.dropzoneHint')}</p>
            </Dragger>
          </Form.Item>

          {preview.length > 0 && (
            <Form.Item label={t('ticketImport.preview')}>
              <Table
                dataSource={preview}
                columns={previewColumns}
                pagination={false}
                scroll={{ x: 800 }}
                size="small"
                rowKey={(record, index) => index?.toString() || '0'}
              />
            </Form.Item>
          )}

          <Form.Item label={t('ticketImport.options')}>
            <Space direction="vertical">
              <Form.Item name="importCompanies" valuePropName="checked" noStyle>
                <Checkbox>
                  <Space>
                    <BankOutlined />
                    {t('ticketImport.optionCompanies')}
                  </Space>
                </Checkbox>
              </Form.Item>
              <Form.Item name="sendNotifications" valuePropName="checked" noStyle>
                <Checkbox>
                  <Space>
                    <InfoCircleOutlined />
                    {t('ticketImport.optionNotifications')}
                  </Space>
                </Checkbox>
              </Form.Item>
            </Space>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" size="large" disabled={!file}>
                {t('ticketImport.next')}
              </Button>
              <Button onClick={handleClose}>{t('ticketImport.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      )}

      {currentStep === 1 && (
        <div>
          <Descriptions bordered style={{ marginBottom: 24 }}>
            <Descriptions.Item label={t('ticketImport.summaryFile')} span={3}>
              {file?.name} ({(file?.size || 0) / 1024} KB)
            </Descriptions.Item>
            <Descriptions.Item label={t('ticketImport.summaryEvent')} span={3}>
              {events.find((e) => e.id === form.getFieldValue('eventId'))?.title}
            </Descriptions.Item>
            <Descriptions.Item label={t('ticketImport.summaryRows')} span={3}>
              {t('ticketImport.rowsValue', { count: totalRows })}
            </Descriptions.Item>
            <Descriptions.Item label={t('ticketImport.summaryOptions')} span={3}>
              <Space>
                {(() => {
                  // #165 — a szervező a VÉGLEGESÍTÉS előtt lássa, melyik
                  // kategóriába megy az egész fájl. Ez az utolsó pont, ahol
                  // még vissza tud lépni; utána már jegyeket kell átsorolni.
                  const key = attendeeTypeLabelKey(form.getFieldValue('attendeeType'));
                  return (
                    <Tag color="blue">{key ? t(key) : form.getFieldValue('attendeeType')}</Tag>
                  );
                })()}
                {form.getFieldValue('importCompanies') && (
                  <Tag color="green">{t('ticketImport.tagCompanies')}</Tag>
                )}
                {form.getFieldValue('sendNotifications') && (
                  <Tag color="orange">{t('ticketImport.tagNotifications')}</Tag>
                )}
              </Space>
            </Descriptions.Item>
          </Descriptions>

          <Space>
            <Button onClick={() => setCurrentStep(0)}>{t('ticketImport.back')}</Button>
            <Button
              type="primary"
              loading={importing}
              onClick={() => {
                const formValues = form.getFieldsValue();
                console.log('Form values before import:', formValues);
                console.log('All fields value (true values):', form.getFieldsValue(true));

                // Manual fallback to get values
                const eventId = form.getFieldValue('eventId');
                const importCompanies = form.getFieldValue('importCompanies') ?? false;
                const sendNotifications = form.getFieldValue('sendNotifications') ?? false;

                const manualValues = {
                  eventId,
                  importCompanies,
                  sendNotifications,
                  // #165 — EZT A SORT nem szabad elfelejteni. Ez a gomb nem a
                  // `Form` `onFinish`-ét hívja, hanem kézzel szedi össze a
                  // mezőket, tehát egy új űrlapmező ITT is fel kell hogy
                  // kerüljön. Enélkül a `handleImport` `undefined`-ot kap, és
                  // a `FormData` a „undefined" SZTRINGET küldené — amit a
                  // szerver enum-ellenőrzése 400-zal utasít el, vagyis az
                  // import egészében elhasalna.
                  attendeeType: form.getFieldValue('attendeeType'),
                };

                console.log('Manual values:', manualValues);
                handleImport(manualValues);
              }}
            >
              {t('ticketImport.startImport')}
            </Button>
            <Button onClick={handleClose}>{t('ticketImport.cancel')}</Button>
          </Space>

          {importing && (
            <div style={{ marginTop: 24 }}>
              <Progress percent={50} status="active" />
              <Text type="secondary">{t('ticketImport.importingHint')}</Text>
            </div>
          )}
        </div>
      )}

      {currentStep === 2 && result && (
        <div>
          <Result
            status={result.failed === 0 ? 'success' : 'warning'}
            title={
              result.failed === 0
                ? t('ticketImport.resultSuccessTitle')
                : t('ticketImport.resultWarningTitle')
            }
            extra={[
              <Button type="primary" key="close" onClick={handleClose}>
                {t('ticketImport.close')}
              </Button>,
              <Button key="new" onClick={resetModal}>
                {t('ticketImport.importMore')}
              </Button>,
            ]}
          >
            <div style={{ marginBottom: 24 }}>
              <Descriptions bordered>
                <Descriptions.Item
                  label={
                    <>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />{' '}
                      {t('ticketImport.resultSuccessful')}
                    </>
                  }
                  span={1}
                >
                  {result.success}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <>
                      <CloseCircleOutlined style={{ color: '#f5222d' }} />{' '}
                      {t('ticketImport.resultFailed')}
                    </>
                  }
                  span={1}
                >
                  {result.failed}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <>
                      <InfoCircleOutlined style={{ color: '#faad14' }} />{' '}
                      {t('ticketImport.resultSkipped')}
                    </>
                  }
                  span={1}
                >
                  {result.skipped.existingTickets}
                </Descriptions.Item>
                <Descriptions.Item label={t('ticketImport.resultNewTickets')} span={1}>
                  <Tag color="green" icon={<TagsOutlined />}>
                    {result.created.tickets}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('ticketImport.resultNewUsers')} span={1}>
                  <Tag color="blue" icon={<TeamOutlined />}>
                    {result.created.users}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('ticketImport.resultNewCompanies')} span={1}>
                  <Tag color="purple" icon={<BankOutlined />}>
                    {result.created.companies}
                  </Tag>
                </Descriptions.Item>
                {/* Csak ha a szervező kérte a küldést. Enélkül a jelölőnégyzet
                    megint csak egy ígéret lenne: ez az egyetlen hely, ahol
                    látszik, hogy a levelek tényleg elmentek-e (#104). */}
                {result.notifications && (
                  <Descriptions.Item label={t('ticketImport.resultMailsSent')} span={1}>
                    <Tag color="green" icon={<MailOutlined />}>
                      {result.notifications.sent}
                    </Tag>
                    {result.notifications.failed > 0 && (
                      <Tag color="red">{result.notifications.failed}</Tag>
                    )}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>

            {result.notifications && result.notifications.failed > 0 && (
              <Alert
                style={{ marginBottom: 16 }}
                message={t('ticketImport.mailsFailedTitle', {
                  count: result.notifications.failed,
                })}
                description={
                  <div style={{ maxHeight: 160, overflow: 'auto' }}>
                    <div style={{ marginBottom: 8 }}>{t('ticketImport.mailsFailedHint')}</div>
                    {result.notifications.failedTickets.join(', ')}
                  </div>
                }
                type="warning"
                showIcon
              />
            )}

            {result.notifications && result.notifications.withoutQr.length > 0 && (
              <Alert
                style={{ marginBottom: 16 }}
                message={t('ticketImport.mailsNoQrTitle', {
                  count: result.notifications.withoutQr.length,
                })}
                description={
                  <div style={{ maxHeight: 160, overflow: 'auto' }}>
                    <div style={{ marginBottom: 8 }}>{t('ticketImport.mailsNoQrHint')}</div>
                    {result.notifications.withoutQr.join(', ')}
                  </div>
                }
                type="warning"
                showIcon
              />
            )}

            {result.notifications && result.notifications.skippedNoEmail > 0 && (
              <Alert
                style={{ marginBottom: 16 }}
                message={t('ticketImport.mailsNoAddress', {
                  count: result.notifications.skippedNoEmail,
                })}
                type="info"
                showIcon
              />
            )}

            {result.errors.length > 0 && (
              <Alert
                message={t('ticketImport.errorsTitle')}
                description={
                  <div style={{ maxHeight: 200, overflow: 'auto' }}>
                    {result.errors.map((error, index) => (
                      <div key={index}>
                        {t('ticketImport.errorRow', { row: error.row, error: error.error })}
                      </div>
                    ))}
                  </div>
                }
                type="error"
                showIcon
              />
            )}
          </Result>
        </div>
      )}
    </Modal>
  );
};

export default TicketImportModal;
